import { Injectable } from '@angular/core'
import { catchError, map, Observable, of, tap } from 'rxjs'
import { WordPairDicService } from 'src/app/service/data/word-pair-dic.service'
import { BookmarkStateService } from 'src/app/service/state/bookmark-state.service'
import { DictionaryStateService } from 'src/app/service/state/dictionary-state.service'
import { HomeStateService } from 'src/app/service/state/home-state.service'
import { LibraryStateService } from 'src/app/service/state/library-state.service'
import { ContentTypes } from 'src/app/shared/enums/enums'
import { CoreCommand } from '../base/core-command'
import { CommandResultService } from '../decorators/command-result.decorator'

export interface CategoryData {
  id: number
  label: string
}

export interface TagData {
  id: number
  label: string
}

export interface DictionaryData {
  id: number
  label: string
}
export interface WordData {
  language: string
  article?: string
  word: string
  pos?: string
  definition?: string
  example?: string
  category?: CategoryData
  tags?: TagData[]
}

export interface DeleteWordPairDicArgs {
  content_type_id: number
  object_id: number
  collection_id?: number
}

@Injectable({
  providedIn: 'root',
})
export class DeleteWordPairDicCommand extends CoreCommand<DeleteWordPairDicArgs, any> {
  readonly id = 'delete-word-pair-dic'

  constructor(
    private commandResultService: CommandResultService,
    private wordPairDicService: WordPairDicService,
    private libraryState: LibraryStateService,
    private dictionaryState: DictionaryStateService,
    private homeState: HomeStateService,
    private bookmarkState: BookmarkStateService,
  ) {
    super()
  }

  override checkArguments(args?: DeleteWordPairDicArgs): boolean {
    return !!args
  }

  protected doExecute(args?: DeleteWordPairDicArgs): Observable<boolean> {
    return this.wordPairDicService.delete(args!).pipe(
      tap(result => this.applyStateUpdates(args!, result)),
      tap(result => {
        this.emitResult({ success: result.success, message: result.message, data: result.data })
        this.commandResultService.handleResult(this)
      }),
      map(result => result.success),
      catchError(error => {
        this.emitResult({
          success: false,
          message: error instanceof Error ? error.message : 'Failed to delete word/pair',
        })
        this.commandResultService.handleError(error)
        return of(false)
      }),
    )
  }

  private applyStateUpdates(args: DeleteWordPairDicArgs, result: any): void {
    if (!result.success) return

    // Remove from dictionary state based on content type
    if (args.content_type_id === ContentTypes.WORD) {
      this.dictionaryState.removeWord(args.object_id)
      // Remove from bookmark state
      this.bookmarkState.removeBookmarkItem(args.object_id, 'word')
    } else if (args.content_type_id === ContentTypes.PAIR) {
      this.dictionaryState.removePair(args.object_id)
      // Remove from bookmark state
      this.bookmarkState.removeBookmarkItem(args.object_id, 'pair')
    } else if (args.content_type_id === ContentTypes.DIC) {
      // Remove from bookmark state for dictionary
      this.bookmarkState.removeBookmarkItem(args.object_id, 'dictionary')
    }
  }
}
